import React from "react";
import { Form, useFormikContext } from "formik";
import NavigationalButtons from "../Components/NavigationalButtons";
import { Stack, Checkbox, Text } from "@fluentui/react";
import NoteComponent from "../../../../Util/NoteComponent";

const roleDefinitions = [
    {
        id: 11,
        title: "Reports Access",
        subtext: "Determines if the old reports console is accessible at all.",
    },
    {
        id: 12,
        title: "User Level Detail",
        subtext:
            "Determines if the report user can see reports which show specific platform user's information.",
    },
    {
        id: 75,
        title: "User Level Detail",
        subtext:
            "Determines if the report user can see reports which show specific platform user's information.",
    },
    {
        id: 13,
        title: "Sensitive Batch Files",
        subtext:
            "Determines if the user can see the Sensitive Batch Files aka Counseling Reports for download which contain the COD user data. This data contains SSNs and other PII.",
    },
];

const AccessRolesStep = ({ prevOnClick }) => {
    const { values, setFieldValue, isSubmitting } = useFormikContext();
    const reportUserRoles = values.reportUserRoles || [];

    const handleRoleChange = (roleId, checked) => {
        if (checked) {
            // Add role if not already present
            if (!reportUserRoles.includes(roleId)) {
                setFieldValue("reportUserRoles", [...reportUserRoles, roleId]);
            }
        } else {
            // Remove role
            setFieldValue(
                "reportUserRoles",
                reportUserRoles.filter((id) => id !== roleId),
            );
        }
    };

    return (
        <Form>
            <Stack
                tokens={{ childrenGap: 20 }}
                styles={{ root: { padding: "20px 0", maxWidth: 600 } }}
            >
                {/* Reports Site Section */}
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    <Text variant="large" block styles={{ root: { fontWeight: 600 } }}>
                        Old Reports Site
                    </Text>
                    {roleDefinitions
                        .filter((role) => role.id !== 75 && role.id !== 13)
                        .map((role) => (
                            <Checkbox
                                key={role.id}
                                label={role.title}
                                checked={reportUserRoles.includes(role.id)}
                                onChange={(ev, checked) => handleRoleChange(role.id, checked)}
                                styles={{
                                    root: { marginBottom: 12 },
                                    checkbox: { marginRight: 12 },
                                }}
                                onRenderLabel={() => (
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        <Text
                                            variant="medium"
                                            styles={{ root: { fontWeight: 600 } }}
                                        >
                                            {role.title}
                                        </Text>
                                        <Text
                                            variant="small"
                                            styles={{ root: { color: "#605E5C" } }}
                                        >
                                            {role.subtext}
                                        </Text>
                                    </Stack>
                                )}
                            />
                        ))}
                </Stack>

                {/* Client Console Section */}
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    <Text variant="large" block styles={{ root: { fontWeight: 600 } }}>
                        Client Console
                    </Text>
                    {roleDefinitions
                        .filter((role) => role.id === 75)
                        .map((role) => (
                            <Checkbox
                                key={role.id}
                                label={role.title}
                                checked={reportUserRoles.includes(role.id)}
                                onChange={(ev, checked) => handleRoleChange(role.id, checked)}
                                styles={{
                                    root: { marginBottom: 12 },
                                    checkbox: { marginRight: 12 },
                                }}
                                onRenderLabel={() => (
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        <Text
                                            variant="medium"
                                            styles={{ root: { fontWeight: 600 } }}
                                        >
                                            {role.title}
                                        </Text>
                                        <Text
                                            variant="small"
                                            styles={{ root: { color: "#605E5C" } }}
                                        >
                                            {role.subtext}
                                        </Text>
                                    </Stack>
                                )}
                            />
                        ))}
                    <NoteComponent
                        message={
                            <span>
                                A user account must also be setup in Auth0 to allow a report user to
                                access the Client Console. Please get in touch with IT if you do not
                                know how or are not able to do this.
                            </span>
                        }
                        isMultiline={true}
                    />

                    <NoteComponent
                        message={
                            <span>
                                Creating a report user will NOT automatically enable reports at the
                                cobrand level. This must be done in both the{" "}
                                <a
                                    href="https://admin.igrad.com/CobrandSettings/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Cobrand Settings admin
                                </a>{" "}
                                (see the setting labeled "Reports Enabled") and the
                                <a
                                    href="https://admin2.igrad.com/cobrand-settings-feature/client-console-reports"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    individual report permissions for the cobrand(s)
                                </a>
                                .
                            </span>
                        }
                        isMultiline={true}
                    />
                </Stack>

                {/* Shared Section */}
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    <Text variant="large" block styles={{ root: { fontWeight: 600 } }}>
                        Shared
                    </Text>
                    {roleDefinitions
                        .filter((role) => role.id === 13)
                        .map((role) => (
                            <Checkbox
                                key={role.id}
                                label={role.title}
                                checked={reportUserRoles.includes(role.id)}
                                onChange={(ev, checked) => handleRoleChange(role.id, checked)}
                                styles={{
                                    root: { marginBottom: 12 },
                                    checkbox: { marginRight: 12 },
                                }}
                                onRenderLabel={() => (
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        <Text
                                            variant="medium"
                                            styles={{ root: { fontWeight: 600 } }}
                                        >
                                            {role.title}
                                        </Text>
                                        <Text
                                            variant="small"
                                            styles={{ root: { color: "#605E5C" } }}
                                        >
                                            {role.subtext}
                                        </Text>
                                    </Stack>
                                )}
                            />
                        ))}
                    <NoteComponent
                        message={
                            <span>
                                The Sensitive Batch Files feature is available on both the old
                                reports site and the new Client Console. However, it is currently
                                only enabled on the old reports site, with Client Console support
                                coming soon.
                            </span>
                        }
                        isMultiline={true}
                    />

                    <NavigationalButtons
                        onPrevHandler={prevOnClick}
                        onNextProps={{ type: "submit", text: "Save" }}
                        loading={isSubmitting}
                    />
                </Stack>
            </Stack>
        </Form>
    );
};

export default AccessRolesStep;
